import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconExternalLink, IconShieldCheck, IconVolume, IconVolumeOff } from '@tabler/icons-react';
import { useAuth0 } from '@auth0/auth0-react';
import packageJson from '../package.json';
import ConfirmModal from './components/modals/ConfirmModal.jsx';
import AlertModal from './AlertModal.jsx';
import useAutoScanSettings from './hooks/useAutoScanSettings.js';

const parseNameParts = (fullName = '') => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) {
    return { firstName: parts[0] || '', lastName: '' };
  }
  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts[parts.length - 1],
  };
};

const SettingsPage = () => {
  const { user, logout } = useAuth0();
  const {
    isAutoScan,
    showUnauthorizedAlert,
    setShowUnauthorizedAlert,
    updateAutoScanStatusAsync,
  } = useAutoScanSettings();

  const [confirmOpened, setConfirmOpened] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('driveOffSoundEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [allVehicleSoundEnabled, setAllVehicleSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('allVehicleSoundEnabled');
    return saved !== null ? JSON.parse(saved) : false;
  });

  const proModeUnlocked = useMemo(() => localStorage.getItem('proModeUnlocked') === 'true', []);
  const { firstName, lastName } = parseNameParts(user?.name || '');
  const email = user?.email || '';

  const handleSoundToggle = () => {
    const nextValue = !soundEnabled;
    setSoundEnabled(nextValue);
    localStorage.setItem('driveOffSoundEnabled', JSON.stringify(nextValue));
  };

  const handleAllVehicleSoundToggle = () => {
    const nextValue = !allVehicleSoundEnabled;
    setAllVehicleSoundEnabled(nextValue);
    localStorage.setItem('allVehicleSoundEnabled', JSON.stringify(nextValue));
  };

  const onConfirmAutoScan = async () => {
    await updateAutoScanStatusAsync(!isAutoScan);
    setConfirmOpened(false);
  };

  const testDriveOffAlert = async () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      if (audioContext.state === 'suspended') await audioContext.resume();

      let isHigh = false;
      let oscillatorRef = null;

      const playSirenCycle = () => {
        if (oscillatorRef) {
          try { oscillatorRef.stop(); } catch (error) {}
        }

        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.frequency.value = isHigh ? 1200 : 800;
        oscillator.type = 'sawtooth';
        gainNode.gain.setValueAtTime(0.4, audioContext.currentTime);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
        oscillatorRef = oscillator;
        isHigh = !isHigh;
      };

      playSirenCycle();
      const intervalId = setInterval(playSirenCycle, 500);
      setTimeout(() => {
        clearInterval(intervalId);
        if (oscillatorRef) {
          try { oscillatorRef.stop(); } catch (error) {}
        }
        audioContext.close();
      }, 3000);
    } catch (error) {
      console.error('Error playing test alert:', error);
    }
  };

  return (
    <>
      <Box p="xl" style={{ backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
        <Paper shadow="xs" radius="md" p={0} style={{ overflow: 'hidden', textAlign: 'left' }}>
          <Box px="xl" py="lg" style={{ borderBottom: '1px solid #E2E8F0' }}>
            <Title order={2} ta="left">Settings</Title>
          </Box>

          <Box px="xl" pb="lg">
            <Group align="flex-start" gap="lg" wrap="nowrap">
              <Avatar src={user?.picture} size={120} radius="xl" />
              <Stack gap={4}>
                <Title order={3} ta="left">Profile Picture</Title>
                <Text c="dimmed" size="sm" ta="left">Supports PNGs, JPEGs and GIFs under 3MB</Text>
              </Stack>
            </Group>
          </Box>

          <Divider />

          <Box px="xl" py="xl">
            <Title order={3} mb="lg" ta="left">Basic details</Title>
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
              <TextInput label="First Name" value={firstName} readOnly />
              <TextInput label="Last Name" value={lastName} readOnly />
            </SimpleGrid>

            <TextInput label="Email Address" value={email} mt="md" readOnly />
          </Box>

          <Divider />

          <Box px="xl" py="xl">
            <Title order={3} mb="lg" ta="left">Available options</Title>
            <Stack gap="lg">
              <Group justify="space-between" align="center" wrap="nowrap">
                <div>
                  <Text fw={600}>Drive-Off Alert Sound</Text>
                  <Text c="dimmed" size="sm">Play urgent alert when drive-off vehicles are detected</Text>
                </div>
                <Switch
                  checked={soundEnabled}
                  onChange={handleSoundToggle}
                  onLabel={<IconVolume size={14} />}
                  offLabel={<IconVolumeOff size={14} />}
                />
              </Group>

              <Group justify="space-between" align="center" wrap="nowrap">
                <div>
                  <Text fw={600}>All Vehicle Alert Sound</Text>
                  <Text c="dimmed" size="sm">Play short beep when any vehicle is detected</Text>
                </div>
                <Switch checked={allVehicleSoundEnabled} onChange={handleAllVehicleSoundToggle} />
              </Group>

              <Group justify="space-between" align="center" wrap="nowrap">
                <div>
                  <Text fw={600}>Auto Scan</Text>
                  <Text c="dimmed" size="sm">Automatically scan vehicle registrations</Text>
                </div>
                <Switch checked={isAutoScan} onChange={() => setConfirmOpened(true)} />
              </Group>

              {proModeUnlocked && (
                <>
                  <Group justify="space-between" align="center" wrap="nowrap">
                    <div>
                      <Text fw={600}>Drive-Off Risk Dashboard</Text>
                      <Text c="dimmed" size="sm">Open the advanced monitoring dashboard</Text>
                    </div>
                    <Button
                      size="sm"
                      rightSection={<IconExternalLink size={14} />}
                      onClick={() => window.open('/#/reportDashboard', '_blank')}
                    >
                      View Dashboard
                    </Button>
                  </Group>

                  <Group justify="space-between" align="center" wrap="nowrap">
                    <div>
                      <Text fw={600}>Test Drive-Off Alert</Text>
                      <Text c="dimmed" size="sm">Play the siren test for operational checks</Text>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      leftSection={<IconShieldCheck size={14} />}
                      onClick={testDriveOffAlert}
                      disabled={!soundEnabled}
                    >
                      Test Alert
                    </Button>
                  </Group>
                </>
              )}

              <Group justify="space-between" align="center" wrap="nowrap">
                <div>
                  <Text fw={600}>Software Version</Text>
                  <Text c="dimmed" size="sm">Current DriveOffShield web client version</Text>
                </div>
                <Text fw={600} c="gray.7">{`v${packageJson.version}`}</Text>
              </Group>
            </Stack>
          </Box>
        </Paper>
      </Box>

      <ConfirmModal
        opened={confirmOpened}
        onClose={() => setConfirmOpened(false)}
        isAutoScan={isAutoScan}
        onConfirm={onConfirmAutoScan}
      />

      <AlertModal
        show={showUnauthorizedAlert}
        handleOnClick={() => {
          setShowUnauthorizedAlert(false);
          logout({ logoutParams: { returnTo: window.location.origin } });
        }}
        headerTitle="Authorisation Pending"
        messageBody="Your user roles have not been assigned yet. Please contact your administrator to request the necessary access."
        buttonText="Logout"
      />
    </>
  );
};

export default SettingsPage;
